import * as z from "zod";
import {
  disputeReason,
  disputeSource,
  disputeType,
} from "./dispute-definition";

export const reasonBreakdownSchema = z.object({
  reason: z.enum(disputeReason),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
});

export type ReasonBreakdown = z.infer<typeof reasonBreakdownSchema>;

export const cardBrandBreakdownSchema = z.object({
  cardBrand: z.string(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
});

export type CardBrandBreakdown = z.infer<typeof cardBrandBreakdownSchema>;

export const monthlyStatsSchema = z.object({
  month: z.string(),
  totalCount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  openCount: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  lostAmount: z.coerce.number(),
});

export type MonthlyStats = z.infer<typeof monthlyStatsSchema>;

export const sourceBreakdownSchema = z.object({
  source: z.enum(disputeSource),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  lostAmount: z.coerce.number(),
});

export type SourceBreakdown = z.infer<typeof sourceBreakdownSchema>;

export const aiPerformanceSchema = z.object({
  aiWonCount: z.coerce.number(),
  aiLostCount: z.coerce.number(),
  aiWonAmount: z.coerce.number(),
  aiLostAmount: z.coerce.number(),
  manualWonCount: z.coerce.number(),
  manualLostCount: z.coerce.number(),
  manualWonAmount: z.coerce.number(),
  manualLostAmount: z.coerce.number(),
});

export type AiPerformance = z.infer<typeof aiPerformanceSchema>;

/**
 * All-time Reclaim-vs-not comparison, USD-aggregated. "ai" = disputes where
 * Reclaim generated evidence (ai_evidence_generated_at IS NOT NULL);
 * "manual" = everything else, including pre-Reclaim history. Open = actively
 * fought (needs_response/under_review), AI-touched only.
 */
export const reclaimImpactSchema = z.object({
  aiWonCount: z.coerce.number(),
  aiLostCount: z.coerce.number(),
  aiOpenCount: z.coerce.number(),
  aiOpenAmount: z.coerce.number(),
  manualWonCount: z.coerce.number(),
  manualLostCount: z.coerce.number(),
  /** MIN(ai_evidence_generated_at) for the account, ISO timestamp. */
  reclaimStartedAt: z.string().nullable(),
});

export type ReclaimImpact = z.infer<typeof reclaimImpactSchema>;

/**
 * Monthly finalized outcomes split by Reclaim attribution, bucketed by
 * finalization month (not initiation month) so each point is a rate over
 * decided disputes only.
 */
export const monthlyWinRateSchema = z.object({
  month: z.string(),
  aiWonCount: z.coerce.number(),
  aiLostCount: z.coerce.number(),
  manualWonCount: z.coerce.number(),
  manualLostCount: z.coerce.number(),
});

export type MonthlyWinRate = z.infer<typeof monthlyWinRateSchema>;

export const dashboardStatsSchema = z.object({
  totalCount: z.coerce.number(),
  needsAttentionCount: z.coerce.number(),
  needsResponseCount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  evidenceGeneratedCount: z.coerce.number(),
  underReviewCount: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  finalizedAmount: z.coerce.number(),
  needsResponseAmount: z.coerce.number(),
  evidenceGeneratedAmount: z.coerce.number(),
  underReviewAmount: z.coerce.number(),
  currency: z.string(),
  reasonBreakdown: z.array(reasonBreakdownSchema).nullish(),
  cardBrandBreakdown: z.array(cardBrandBreakdownSchema).nullish(),
  monthlyStats: z.array(monthlyStatsSchema).nullish(),
  sourceBreakdown: z.array(sourceBreakdownSchema).nullish(),
  aiPerformance: aiPerformanceSchema.nullish(),
  reclaimImpact: reclaimImpactSchema.nullish(),
  monthlyWinRate: z.array(monthlyWinRateSchema).nullish(),
  errors: z.array(z.string()).nullish(),
  priorPeriod: z
    .object({
      totalCount: z.coerce.number(),
      needsAttentionCount: z.coerce.number(),
      needsResponseCount: z.coerce.number(),
      wonCount: z.coerce.number(),
      lostCount: z.coerce.number(),
      totalAmount: z.coerce.number(),
      wonAmount: z.coerce.number(),
      finalizedAmount: z.coerce.number(),
    })
    .nullish(),
});

export type DashboardStats = z.infer<typeof dashboardStatsSchema>;

export const statsPerCurrencySchema = z.object({
  currency: z.string(),
  totalCount: z.coerce.number(),
  needsAttentionCount: z.coerce.number(),
  needsResponseCount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  evidenceGeneratedCount: z.coerce.number(),
  underReviewCount: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  finalizedAmount: z.coerce.number(),
  evidenceGeneratedAmount: z.coerce.number(),
  needsResponseAmount: z.coerce.number(),
  underReviewAmount: z.coerce.number(),
});

export interface StatsPerCurrency
  extends z.infer<typeof statsPerCurrencySchema> {}

export const statsPerAccountAndCurrencySchema = statsPerCurrencySchema.extend({
  accountId: z.bigint(),
  /**
   * Sum of `amount` for chargebacks initiated in the trailing 365 days,
   * excluding Shopify-protected. Used by the per-merchant deal-size
   * calculation on the admin Merchants tab — separate from totalAmount
   * (which is all-time, all dispute types).
   */
  trailingYearChargebackAmount: z.coerce.number(),
  /**
   * Count of AI-touched, non-protected disputes for this (account, currency)
   * that finalized as 'won' with ai_evidence_generated_at in the last 30
   * days. Used to compute the merchant's own win rate for deal sizing,
   * independently of the dashboard's denominator toggle.
   */
  winRateWindowWonCount: z.coerce.number(),
  /** Mirror of winRateWindowWonCount for status='lost'. */
  winRateWindowLostCount: z.coerce.number(),
  /**
   * Earliest chargeback `initiated_at` for this (account, currency).
   * Aggregated across currencies in the handler to scale partial-year
   * volume (merchants younger than 365d). Null when the account has no
   * chargebacks at all.
   */
  firstChargebackInitiatedAt: z.coerce.date().nullable(),
});

export interface StatsPerAccountAndCurrency
  extends z.infer<typeof statsPerAccountAndCurrencySchema> {}

export const trailingYearChargebackByAccountCurrencySchema = z.object({
  accountId: z.bigint(),
  currency: z.string(),
  chargebackCount: z.coerce.number(),
  chargebackAmount: z.coerce.number(),
});

export interface TrailingYearChargebackByAccountCurrency
  extends z.infer<typeof trailingYearChargebackByAccountCurrencySchema> {}

export const trailingYearDisputesByAccountCurrencyAndTypeSchema = z.object({
  accountId: z.bigint(),
  currency: z.string(),
  type: z.enum(disputeType),
  disputeCount: z.coerce.number(),
  disputeAmount: z.coerce.number(),
});

export interface TrailingYearDisputesByAccountCurrencyAndType
  extends z.infer<typeof trailingYearDisputesByAccountCurrencyAndTypeSchema> {}

export const reasonBreakdownByCurrencySchema = z.object({
  reason: reasonBreakdownSchema.shape.reason,
  currency: z.string(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
});

export interface ReasonBreakdownByCurrency
  extends z.infer<typeof reasonBreakdownByCurrencySchema> {}

export const monthlyStatsByCurrencySchema = z.object({
  month: z.coerce.date(),
  currency: z.string(),
  totalCount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  openCount: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  lostAmount: z.coerce.number(),
});

export interface MonthlyStatsByCurrency
  extends z.infer<typeof monthlyStatsByCurrencySchema> {}

export const sourceBreakdownByCurrencySchema = z.object({
  source: z.enum(disputeSource),
  currency: z.string(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  lostAmount: z.coerce.number(),
});

export interface SourceBreakdownByCurrency
  extends z.infer<typeof sourceBreakdownByCurrencySchema> {}

export const reclaimImpactByCurrencySchema = z.object({
  currency: z.string(),
  aiWonCount: z.coerce.number(),
  aiLostCount: z.coerce.number(),
  aiOpenCount: z.coerce.number(),
  aiOpenAmount: z.coerce.number(),
  manualWonCount: z.coerce.number(),
  manualLostCount: z.coerce.number(),
  reclaimStartedAt: z.coerce.date().nullable(),
});

export interface ReclaimImpactByCurrency
  extends z.infer<typeof reclaimImpactByCurrencySchema> {}

export const monthlyWinRateByCurrencySchema = z.object({
  month: z.coerce.date(),
  currency: z.string(),
  aiWonCount: z.coerce.number(),
  aiLostCount: z.coerce.number(),
  manualWonCount: z.coerce.number(),
  manualLostCount: z.coerce.number(),
});

export interface MonthlyWinRateByCurrency
  extends z.infer<typeof monthlyWinRateByCurrencySchema> {}

export const aiPerformanceByCurrencySchema = z.object({
  method: z.string(),
  currency: z.string(),
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  wonAmount: z.coerce.number(),
  lostAmount: z.coerce.number(),
});

export interface AiPerformanceByCurrency
  extends z.infer<typeof aiPerformanceByCurrencySchema> {}
